import React, { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Shuffle, Radio, Volume2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useRadio } from "@/context/RadioContext";
import ShareTrack from "@/components/ShareTrack";
import ListenFull from "@/components/ListenFull";

export default function RadioPage() {
  const { lang, t } = useLang();
  const { tracks, current, index, playing, progress, filter, shuffle, setFilter, setShuffle, playTrack, loadTrack, playById, toggle, go } = useRadio();
  const [params] = useSearchParams();
  const handled = useRef(null);

  const deepId = params.get("track");
  useEffect(() => {
    if (!deepId || handled.current === deepId || !tracks.length) return;
    handled.current = deepId;
    if (filter !== "all") setFilter("all");
    playById(deepId);
  }, [deepId, tracks.length, filter, setFilter, playById]);

  useEffect(() => {
    if (!current && tracks.length && !deepId) loadTrack(tracks[0]);
  }, [current, tracks, deepId, loadTrack]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8" data-testid="radio-page">
      <header className="mb-6">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold mb-2">
          <Radio className="w-4 h-4" /> {t("radio")}
        </div>
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-ink tracking-tight">{t("radio_title")}</h1>
        <p className="text-muted-foreground text-sm mt-2">{t("radio_tagline").replace("{n}", tracks.length)}</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">
        <section className="min-w-0 w-full rounded-3xl bg-ink text-sand p-5 lg:sticky lg:top-24" data-testid="radio-now-playing">
          {current ? (
            <>
              <div className="relative rounded-2xl overflow-hidden aspect-square max-h-[60vw] lg:max-h-none mx-auto bg-sandDeep/20 mb-4">
                {current.artwork && <img src={current.artwork} alt="" className="w-full h-full object-cover" />}
                <img src={current.country.flag_url} alt="" className="absolute top-3 start-3 w-10 h-7 object-cover rounded border border-white/40 shadow" />
                <span className="absolute top-3 end-3 px-2 py-0.5 rounded-full bg-black/50 text-[10px] uppercase tracking-wider">{t(`song_${current.kind}`)}</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-semibold leading-tight truncate" dir="auto" data-testid="radio-title">{current.title}</div>
                  <div className="text-sm text-sand/70 truncate" dir="auto" data-testid="radio-artist">{current.artist}</div>
                  <Link to={`/?c=${current.country.code}`} className="text-xs text-ochre hover:underline" data-testid="radio-country-link">
                    {current.country.short_names[lang]} →
                  </Link>
                </div>
                <ShareTrack track={current} />
              </div>
              <div className="mt-4 h-1 rounded-full bg-sand/15 overflow-hidden">
                <div className="h-full bg-ochre transition-[width] duration-200" style={{ width: `${progress}%` }} />
              </div>
              <div className="mt-4"><ListenFull song={current} countryCode={current.country.code} /></div>
              <div className="mt-4 flex items-center justify-center gap-3">
                <button onClick={() => setShuffle()} aria-pressed={shuffle} aria-label="shuffle" className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${shuffle ? "bg-ochre text-white" : "bg-sand/10 hover:bg-sand/20"}`} data-testid="radio-shuffle"><Shuffle className="w-4 h-4" /></button>
                <button onClick={() => go(-1)} aria-label="previous" className="w-11 h-11 rounded-full bg-sand/10 hover:bg-sand/20 flex items-center justify-center" data-testid="radio-prev"><SkipBack className="w-5 h-5 rtl:rotate-180" /></button>
                <button onClick={toggle} aria-label={playing ? t("song_pause") : t("song_play")} className="w-14 h-14 rounded-full bg-ochre hover:bg-ochre/90 text-white flex items-center justify-center shadow-lg" data-testid="radio-toggle">
                  {playing ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6 ms-0.5" fill="currentColor" />}
                </button>
                <button onClick={() => go(1)} aria-label="next" className="w-11 h-11 rounded-full bg-sand/10 hover:bg-sand/20 flex items-center justify-center" data-testid="radio-next"><SkipForward className="w-5 h-5 rtl:rotate-180" /></button>
                <span className="w-10 h-10 flex items-center justify-center text-sand/50"><Volume2 className="w-4 h-4" /></span>
              </div>
            </>
          ) : <div className="text-sm text-sand/60">…</div>}
        </section>

        <section className="min-w-0">
          <div className="flex gap-2 mb-3" data-testid="radio-filters">
            {["all", "classic", "modern"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} aria-pressed={filter === f} className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === f ? "bg-ink text-sand border-ink" : "border-stoneLine hover:bg-sandDeep/50"}`} data-testid={`radio-filter-${f}`}>
                {f === "all" ? t("radio_all") : t(`song_${f}`)}
              </button>
            ))}
          </div>
          <ol className="divide-y divide-stoneLine/70 rounded-2xl border border-stoneLine bg-card overflow-hidden" data-testid="radio-playlist">
            {tracks.map((tr, i) => {
              const active = i === index;
              return (
                <li key={tr.id}>
                  <button onClick={() => playTrack(tr)} className={`w-full flex items-center gap-3 px-3 py-2.5 text-start transition-colors ${active ? "bg-ochre/10" : "hover:bg-sandDeep/30"}`} data-testid={`radio-track-${tr.id}`} aria-current={active}>
                    <span className="w-6 text-xs text-muted-foreground tabular-nums">{active && playing ? <Volume2 className="w-4 h-4 text-ochre" /> : i + 1}</span>
                    <img src={tr.artwork} alt="" className="w-10 h-10 rounded-md object-cover bg-sandDeep/40" loading="lazy" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-ink truncate" dir="auto">{tr.title}</span>
                      <span className="block text-xs text-muted-foreground truncate" dir="auto">{tr.artist}</span>
                    </span>
                    <img src={tr.country.flag_url} alt="" className="w-6 h-4 object-cover rounded-sm border border-stoneLine" />
                    <span className="hidden sm:block text-xs text-muted-foreground w-24 truncate">{tr.country.short_names[lang]}</span>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${tr.kind === "modern" ? "bg-oasis/10 text-oasis" : "bg-ochre/10 text-ochre"}`}>{t(`song_${tr.kind}`)}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
    </div>
  );
}
